import React, {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Formik, Form, Field, FieldArray, ErrorMessage} from 'formik';
import {toast} from 'react-toastify';
import {IoAdd, IoTrash, IoChevronDown, IoChevronUp} from 'react-icons/io5';
import MultiImages from 'components/multiImages/MultiImages';
import MultiSelect from 'components/multiSelect/MultiSelect';
import {api} from 'api/calls';
import generalExtraActions from 'reduxStore/actions/general';
import {DAYS_OF_WEEK, MAX_FILE_SIZE} from 'pages/grounds/constants';
import * as Yup from 'yup';
import {routes} from 'common';
import './_fieldCreate.scss';
import '../grounds/_groundCreate.scss';
import '../grounds/_groundDetail.scss';
import {ComLoader, Loader} from "../../components";
import {generateHourlySlots} from "./helpers";
import SingleSelect from 'components/singleSelect/SingleSelect';
import {SLOT_DURATIONS} from "./constants";

const fieldValidationSchema = Yup.object().shape({
  ground: Yup.string().required('Ground is required'),
  name: Yup.string().required('Field name is required').min(2, 'At least 2 characters').max(50, 'Max 50 characters'),
  sports: Yup.array().of(Yup.string()).min(1, 'At least one sport must be selected'),
  images: Yup.array().of(
    Yup.object().shape({
      file: Yup.mixed()
        .test('fileSize', 'File size must be less than 2MB', (value) => {
          if (!value) return true;
          return value.size <= MAX_FILE_SIZE;
        })
        .test('fileType', 'Only JPEG, PNG and GIF files are allowed', (value) => {
          if (!value) return true;
          return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
        }),
      image: Yup.string().optional(),
      is_primary: Yup.boolean().default(false),
    })
  ).optional(),
  field_slots: Yup.object().shape(
    DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = Yup.array().of(
        Yup.object().shape({
          start_time: Yup.string().required('Start time is required'),
          end_time: Yup.string().required('End time is required'),
          price: Yup.number().positive('Price must be positive').required('Price is required'),
        }).test('slot-time-validation', 'Start time must be less than end time', function (value) {
          if (!value.start_time || !value.end_time) return true;
          const startTime = new Date(`2000-01-01T${value.start_time}:00`);
          const endTime = new Date(`2000-01-01T${value.end_time}:00`);
          return startTime < endTime;
        })
      ).min(1, `At least one slot must be defined for ${day}`);
      return acc;
    }, {})
  ),
  is_active: Yup.boolean(),
});

const initialSlots = DAYS_OF_WEEK.reduce((acc, day) => {
  acc[day] = [{start_time: '', end_time: '', price: 1000}];
  return acc;
}, {});

const initialValues = {
  ground: '',
  name: '',
  sports: [],
  images: [],
  field_slots: initialSlots,
  is_active: true,
};

function FieldCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef();
  const {
    sports,
    sportsLoading,
    sportsError,
    grounds,
    groundsLoading,
    groundsError
  } = useSelector((state) => state.general);

  const [loading, setLoading] = useState(false);
  const [expandedBasicInfo, setExpandedBasicInfo] = useState(true);
  const [expandedImages, setExpandedImages] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState(true);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [uploadError, setUploadError] = useState('');
  const [selectedGroundId, setSelectedGroundId] = useState('');
  const [slotDuration, setSlotDuration] = useState(60);
  const [slotPrice, setSlotPrice] = useState(1000);
  const isLoading  = sportsLoading || groundsLoading;

  useEffect(() => {
    dispatch(generalExtraActions.getSports());
    dispatch(generalExtraActions.getGrounds({ all: true }));
  }, []);

  // Auto-generate slots if ground is selected and slots are empty
  useEffect(() => {
    const formik = formikRef.current;
    if (!formik) return;
    if (!selectedGroundId) return;
    const values = formik.values;
    const selectedGround = grounds.find(g => g.id === selectedGroundId || g.id === Number(selectedGroundId));
    if (!selectedGround) return;
    const openingTime = selectedGround.opening_time ? selectedGround.opening_time.slice(0,5) : '';
    const closingTime = selectedGround.closing_time ? selectedGround.closing_time.slice(0,5) : '';
    if (!openingTime || !closingTime) return;
    const slots = generateHourlySlots(openingTime, closingTime, slotDuration, slotPrice);
    formik.setValues({
      ...values,
      field_slots: slots
    });
  }, [grounds, selectedGroundId, slotDuration, slotPrice]);

  const handleSetPrimary = (imageIndex, setFieldValue, values) => {
    const updatedImages = values.images.map((image, idx) => ({...image, is_primary: idx === imageIndex}));
    setFieldValue('images', updatedImages);
    toast.success('Primary image updated.');
  };

  const handleDeleteImage = (imageIndex, setFieldValue, values) => {
    const updatedImages = values.images.filter((_, idx) => idx !== imageIndex);
    if (values.images[imageIndex].is_primary && updatedImages.length > 0) {
      updatedImages[0].is_primary = true;
    }
    setFieldValue('images', updatedImages);
    toast.success('Image removed.');
  };

  const validateImage = (file) => {
    if (file.size > MAX_FILE_SIZE) return 'File size must be less than 2MB';
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) return 'Only JPEG, PNG and GIF files are allowed';
    return null;
  };

  const handleImageUpload = (event, setFieldValue, values) => {
    const files = Array.from(event.target.files);
    setUploadError('');
    const validFiles = [];
    const errors = [];
    files.forEach((file) => {
      const error = validateImage(file);
      if (error) errors.push(`${file.name}: ${error}`);
      else validFiles.push(file);
    });
    if (errors.length > 0) {
      setUploadError(errors.join(', '));
      return;
    }
    const newImages = validFiles.map((file, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      image: URL.createObjectURL(file),
      file,
      is_primary: values.images.length === 0 && idx === 0,
    }));
    setFieldValue('images', [...values.images, ...newImages]);
  };

  const handleDayExpansion = (day) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(day)) newSet.delete(day);
      else newSet.add(day);
      return newSet;
    });
  };

  // Add a helper to expand all sections
  const expandAllSections = () => {
    setExpandedBasicInfo(true);
    setExpandedImages(true);
    setExpandedSlots(true);
    setExpandedDays(new Set(DAYS_OF_WEEK));
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Convert images to base64
      const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
      const convertImagesToBase64 = async (images) => {
        const converted = [];
        for (const img of images) {
          if (img.file) {
            try {
              const base64 = await fileToBase64(img.file);
              converted.push({image: base64, is_primary: img.is_primary});
            } catch (error) {
              toast.error(`Error processing image: ${img.file.name}`);
            }
          }
        }
        return converted;
      };
      const images = await convertImagesToBase64(values.images || []);
      // Prepare slots
      const field_slots = Object.fromEntries(
        Object.entries(values.field_slots).map(([day, slots]) => [
          day,
          slots.map((slot) => ({
            ...slot,
            price: slot.price ? Number(slot.price) : null,
          })),
        ])
      );
      // Prepare payload
      const payload = {
        ground: values.ground,
        name: values.name,
        sports: values.sports,
        images,
        field_slots,
        is_active: values.is_active,
      };
      await api.createField(payload);
      toast.success('Field created successfully!');
      navigate(routes.SLOTS);
    } catch (error) {
      console.log(error);
      toast.error('Error creating field.');
      expandAllSections();
    } finally {
      setLoading(false);
    }
  };

  // Expand all sections if there are validation errors after submit
  const handleValidate = (values) => {
    // This function is called by Formik after validation
    // If there are errors, expand all sections
    return {};
  };

  if (!isLoading && (sportsError || groundsError)) {
    return <div className="field-create-page">
      <div>Error loading data.</div>
    </div>;
  }
  
  return (
    <div className="field-create-page ground-detail-page detail-page">
      {isLoading && <Loader />}
      <div className="header">
        <h1>Create New Field</h1>
        <p className="subtitle">Add a new field to a ground</p>
      </div>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={fieldValidationSchema}
        validate={handleValidate}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validateOnChange={false}
      >
        {({values, errors, touched, setFieldValue, validateForm}) => (
          <Form className="custom-form">
              {/* Basic Information Section */}
              <div className="section-header">
                <div className="section-title-wrapper" onClick={() => setExpandedBasicInfo((v) => !v)}
                     style={{cursor: 'pointer'}}>
                  <button type="button" className="expand-btn" onClick={e => {
                    e.stopPropagation();
                    setExpandedBasicInfo(v => !v);
                  }} aria-label={expandedBasicInfo ? 'Collapse basic information' : 'Expand basic information'}>
                    <span className={`expand-icon${expandedBasicInfo ? ' expanded' : ''}`}>{expandedBasicInfo ?
                      <IoChevronUp/> : <IoChevronDown/>}</span>
                  </button>
                  <h2>Basic Information</h2>
                </div>
              </div>
              {expandedBasicInfo && (
                <div className="basic-info-content">
                  <div className="form-group">
                    <label htmlFor="ground">Ground <span className="required">*</span></label>
                    <SingleSelect
                      options={grounds.map(g => ({ label: g.name, value: g.id }))}
                      value={values.ground}
                      onChange={val => {
                        setFieldValue('ground', val);
                        setSelectedGroundId(val);
                      }}
                      placeholder="Select a ground"
                      isDisabled={loading || groundsLoading}
                    />
                    <ErrorMessage name="ground" component="div" className="error-message" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="name">Field Name <span className="required">*</span></label>
                    <Field type="text" name="name"
                           className={`form-control${touched.name && errors.name ? ' is-invalid' : ''}`}
                           placeholder="Enter field name"/>
                    <ErrorMessage name="name" component="div" className="error-message"/>
                  </div>
                  <div className="form-group">
                    <label>Sports <span className="required">*</span></label>
                    <MultiSelect
                      options={sports.map(s => ({label: s.name, value: s.id}))}
                      value={sports.filter(s => values.sports.includes(s.id)).map(s => ({label: s.name, value: s.id}))}
                      onChange={selected => setFieldValue('sports', selected.map(opt => opt.value))}
                      placeholder="Select sports..."
                    />
                    <ErrorMessage name="sports" component="div" className="error-message"/>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <Field as="select" name="is_active" className="form-control">
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </Field>
                  </div>
                </div>
              )}

              {/* Images Section */}
              <div className="section-header">
                <div className="section-title-wrapper" onClick={() => setExpandedImages((v) => !v)}
                     style={{cursor: 'pointer'}}>
                  <button type="button" className="expand-btn" onClick={e => {
                    e.stopPropagation();
                    setExpandedImages(v => !v);
                  }} aria-label={expandedImages ? 'Collapse images' : 'Expand images'}>
                    <span className={`expand-icon${expandedImages ? ' expanded' : ''}`}>{expandedImages ? <IoChevronUp/> :
                      <IoChevronDown/>}</span>
                  </button>
                  <h2>Field Images</h2>
                </div>
              </div>
              {expandedImages && (
                <div className="images-content">
                  <MultiImages
                    images={values.images}
                    onSetPrimary={idx => handleSetPrimary(idx, setFieldValue, values)}
                    onDelete={idx => handleDeleteImage(idx, setFieldValue, values)}
                    onUpload={e => handleImageUpload(e, setFieldValue, values)}
                    loading={loading}
                    errors={errors.images}
                    title="Field Images"
                  />
                  {uploadError && <div className="upload-error">{uploadError}</div>}
                </div>
              )}

              {/* Time Slots Section */}
              <div className="section-header">
                <div className="section-title-wrapper" onClick={() => setExpandedSlots((v) => !v)}
                     style={{cursor: 'pointer'}}>
                  <button type="button" className="expand-btn" onClick={e => {
                    e.stopPropagation();
                    setExpandedSlots(v => !v);
                  }} aria-label={expandedSlots ? 'Collapse time slots' : 'Expand time slots'}>
                    <span className={`expand-icon${expandedSlots ? ' expanded' : ''}`}>{expandedSlots ? <IoChevronUp/> :
                      <IoChevronDown/>}</span>
                  </button>
                  <h2>Time Slots</h2>
                </div>
              </div>
              {expandedSlots && (
                <div className="slots-section">
                  <div className="auto-slot-info">
                    <strong>Tip:</strong> Add time slots for each day. You can add/remove slots as needed.
                  </div>
                  {/* Slot Duration & Slot Price Fields */}
                  <div className="form-group slot-duration-price-group" style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="slotDuration">Slot Duration</label>
                      <select
                        id="slotDuration"
                        className="form-control"
                        value={slotDuration}
                        onChange={e => setSlotDuration(Number(e.target.value))}
                        disabled={loading || groundsLoading}
                      >
                        {SLOT_DURATIONS.map(dur => (
                          <option key={dur} value={dur}>{dur} min</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="slotPrice">Slot Price</label>
                      <input
                        id="slotPrice"
                        type="number"
                        className="form-control"
                        value={slotPrice}
                        min={0}
                        onChange={e => setSlotPrice(Number(e.target.value))}
                        disabled={loading || groundsLoading}
                      />
                    </div>
                  </div>
                  {DAYS_OF_WEEK.map(day => (
                    <div key={day} className="day-slots">
                      <div className="day-header" onClick={() => handleDayExpansion(day)} style={{cursor: 'pointer'}}>
                        <div className="day-title-section">
                          <span
                            className={`expand-btn${expandedDays.has(day) ? ' expanded' : ''}`}>{expandedDays.has(day) ?
                            <IoChevronUp/> : <IoChevronDown/>}</span>
                          <h5>{day.charAt(0).toUpperCase() + day.slice(1)}</h5>
                          <span className="slot-count">({values.field_slots[day].length} slots)</span>
                        </div>
                      </div>
                      {expandedDays.has(day) && (
                        <div className="day-slots-content">
                          <FieldArray name={`field_slots.${day}`}>
                            {({push, remove}) => (
                              <div className="slots-table-container">
                                <table className="slots-table">
                                  <thead>
                                  <tr>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                    <th>Price (PKR)</th>
                                    <th>Actions</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  {values.field_slots[day].map((slot, slotIndex) => [
                                    <tr key={slotIndex} className="slot-row">
                                      <td>
                                        <Field type="time" name={`field_slots.${day}.${slotIndex}.start_time`}
                                               className="form-control"/>
                                        <ErrorMessage name={`field_slots.${day}.${slotIndex}.start_time`} component="div"
                                                      className="error-message"/>
                                      </td>
                                      <td>
                                        <Field type="time" name={`field_slots.${day}.${slotIndex}.end_time`}
                                               className="form-control"/>
                                        <ErrorMessage name={`field_slots.${day}.${slotIndex}.end_time`} component="div"
                                                      className="error-message"/>
                                      </td>
                                      <td>
                                        <Field type="number" name={`field_slots.${day}.${slotIndex}.price`}
                                               className="form-control"/>
                                        <ErrorMessage name={`field_slots.${day}.${slotIndex}.price`} component="div"
                                                      className="error-message"/>
                                      </td>
                                      <td>
                                        <button type="button" className="remove-slot-btn" onClick={() => remove(slotIndex)}>
                                          <IoTrash/>
                                        </button>
                                      </td>
                                    </tr>,
                                    (slot.start_time && slot.end_time && new Date(`2000-01-01T${slot.start_time}:00`) >= new Date(`2000-01-01T${slot.end_time}:00`)) && (
                                      <tr key={`error-${slotIndex}`}>
                                        <td colSpan={4}>
                                          <div className="error-message slot-time-error">
                                            Slot {slotIndex + 1}: Start time must be less than end time
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  ])}
                                  <tr>
                                    <td colSpan="4">
                                      <button type="button" className="add-slot-btn" onClick={() => push({start_time: '', end_time: '', price: 1000})}>
                                        <IoAdd/> Add Slot
                                      </button>
                                    </td>
                                  </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Actions Section */}
              <div className="actions-section">
                <button
                  className="submit-btn primary"
                  type="submit"
                  disabled={loading}
                  onClick={async () => {
                    const formErrors = await validateForm();
                    if (Object.keys(formErrors).length > 0) {
                      expandAllSections();
                      toast.error('Please fix the highlighted errors.');
                    } else {
                      await handleSubmit(values);
                    }
                  }}
                >
                  {loading ? <ComLoader/> : 'Create Field'}
                </button>
                <button
                  className="submit-btn secondary"
                  type="button"
                  onClick={() => navigate(routes.SLOTS)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </Form>
          )
        }
      </Formik>
    </div>
  );
}

export default FieldCreate;